<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import WorkspaceRail from './WorkspaceRail.vue';
import WorkspaceRailDot from './WorkspaceRailDot.vue';
import BrandLogo from '#/components/layout/BrandLogo.vue';
import { buttonVariants } from '#/components/ui';
import { useLogout } from '#/composables/useLogout';
import { useWorkspaceProgress } from '#/composables/useWorkspaceProgress';
import { brand } from '#/config/brand';
import {
  RAIL_ARCHIVE,
  isRailAxisActive,
  isRailItemActive,
  type RailAxis,
  type RailSubItem,
} from '#/data/analysis/workspaceRail';
import { useAuthStore } from '#/stores/auth';

/**
 * WorkspaceShell — AI 입시분석 워크스페이스의 상단 GNB + 본문 프레임.
 *
 * ⚠️ 도메인 전용 셸이라 components/fo/analysis/ 소유(components/layout·ui 는 도메인 무지 원칙 —
 *    docs/frontend-domain-structure-proposal.md §69·71). 이 앱의 **유일한 제품 셸**이다.
 *
 * ## UI 구조 = 진행 레일(구 전폭 메가메뉴 폐기)
 *
 * 5축을 병렬 메가메뉴로 펼치던 구조를, 여정 순서대로 늘어놓는 **진행 레일**로 바꿨다. 근거·기각안은
 * WorkspaceRail 주석 참조(요약: 5축은 실제로 깔때기이고, 대부분 하위 항목이 1개거나 같은 화면 앵커라
 * 전폭 패널이 과했으며, "어디까지 왔나"가 헤더에 없었다).
 *
 *  - 데스크톱(lg↑) 레일·드롭다운은 `WorkspaceRail` 이 소유한다.
 *  - lg 미만은 햄버거 → 세로 레일. 하위 화면이 2종 이상인 축(`axis.items` 보유 — 성적 분석 정시/수시)과
 *    보관함은 아코디언, 나머지 축은 1탭 이동. 어느 축이 아코디언인지는 `workspaceRail.ts` 파생 규칙을 따른다.
 *  - 상태 점(`✓`/`●`/`○`)은 **잠금이 아니다** — 043 SC-007. WorkspaceRailDot 주석 참조.
 *
 * 구 메가메뉴가 쓰던 전체 화면 dim(Teleport)은 **제거했다** — 보관함 300px 드롭다운에 모달급 dim 은
 * 과하고, `useGnbMenu` 의 document 클릭 닫기가 클릭-아웃 역할을 이미 한다.
 *
 * GNB 데이터는 data/analysis/workspaceNav.ts(축 선언) → workspaceRail.ts(레일 순번·재료) 파생.
 * 본문은 default slot(페이지가 <PageShell variant="workspace"> 로 자기 폭·여백 소유).
 */
const auth = useAuthStore();
const route = useRoute();
const { logout } = useLogout();
const { axes } = useWorkspaceProgress();

/** 모바일 패널 열림. */
const mobileOpen = ref(false);
/**
 * 모바일에서 펼친 아코디언 키(축 key 또는 `MOBILE_ARCHIVE_KEY`, null=전부 접힘) — 동시에 하나만.
 * 직접 링크 축(`items` 없음)은 아코디언이 아니라 바로 이동한다.
 */
const mobileAccordion = ref<string | null>(null);
/** 보관함 아코디언 키 — 축 key(`WorkspaceNavKey`)와 겹치지 않는 값. */
const MOBILE_ARCHIVE_KEY = 'archive';

function toggleMobileAccordion(key: string): void {
  mobileAccordion.value = mobileAccordion.value === key ? null : key;
}

function closeMobile() {
  mobileOpen.value = false;
  mobileAccordion.value = null;
}

/** 쿼리의 `kind` 는 배열일 수 있다(vue-router) — 문자열일 때만 판정에 쓴다. */
const currentKind = computed(() =>
  typeof route.query.kind === 'string' ? route.query.kind : undefined,
);

function isAxisActive(axis: Pick<RailAxis, 'route' | 'items'>): boolean {
  return isRailAxisActive(axis, route.path, currentKind.value);
}

function isSubItemActive(item: RailSubItem): boolean {
  return isRailItemActive(item.route, route.path, currentKind.value);
}

/** 링크 이동 후 모바일 메뉴 정리(데스크톱 드롭다운은 WorkspaceRail 이 스스로 닫는다). */
function onNavigate(): void {
  closeMobile();
}

/** 로그아웃 후 워크스페이스 홈(/)으로 복귀. */
function onLogout() {
  return logout(brand.crossLinks.analysis, onNavigate);
}

// 모바일 메뉴를 닫으면 펼쳐둔 아코디언도 초기화 — 다시 열 때 이전 상태가 남지 않게 한다.
watch(mobileOpen, (open) => {
  if (!open) mobileAccordion.value = null;
});
</script>

<template>
  <div class="flex min-h-screen flex-col bg-bg">
    <header class="sticky top-0 z-header border-b border-line bg-white/[0.94] backdrop-blur-[14px]">
      <!-- 헤더 내부 클릭은 외부 클릭 닫기(document 리스너)로 새지 않게 차단 -->
      <div
        class="mx-auto flex h-header max-w-page-workspace items-center justify-between gap-5 px-[18px] md:px-8 xl:px-10"
        @click.stop
      >
        <!-- 브랜드 → 워크스페이스 홈 -->
        <div class="flex flex-none items-center gap-3">
          <RouterLink
            to="/"
            class="flex items-center"
            aria-label="AI 입시분석 홈"
            @click="onNavigate"
          >
            <BrandLogo />
          </RouterLink>
          <span
            class="hidden rounded-full bg-brand px-2.5 py-1 text-[11px] font-extrabold tracking-tight text-white sm:inline-block"
            >AI 입시분석</span
          >
        </div>

        <!-- 데스크톱 GNB(lg↑) — 진행 레일 + 보관함 드롭다운 -->
        <WorkspaceRail @navigate="onNavigate" />

        <!-- 우측: 인증 분기 + 햄버거 -->
        <div class="flex flex-none items-center gap-2">
          <!-- 인증 분기(데스크톱): 링크 안 <button> 중첩 금지 — RouterLink 는 buttonVariants 스타일만. -->
          <template v-if="auth.isAuthenticated">
            <RouterLink
              to="/card"
              class="hidden whitespace-nowrap text-[14.5px] font-semibold text-body transition-colors hover:text-brand lg:inline"
              @click="onNavigate"
            >
              마이페이지
            </RouterLink>
            <button
              type="button"
              :class="
                buttonVariants({ variant: 'line', size: 'sm', class: 'hidden lg:inline-flex' })
              "
              @click="onLogout"
            >
              로그아웃
            </button>
          </template>
          <RouterLink
            v-else
            :to="{ path: '/login', query: { redirect: brand.crossLinks.analysis } }"
            :class="buttonVariants({ variant: 'line', size: 'sm', class: 'hidden lg:inline-flex' })"
            @click="onNavigate"
          >
            로그인
          </RouterLink>

          <!-- 햄버거(lg 미만) -->
          <button
            type="button"
            class="flex h-11 w-11 flex-col items-center justify-center gap-[5px] rounded-lg p-2.5 lg:hidden"
            :aria-label="mobileOpen ? '메뉴 닫기' : '메뉴 열기'"
            :aria-expanded="mobileOpen"
            @click="mobileOpen ? closeMobile() : (mobileOpen = true)"
          >
            <span class="block h-0.5 w-[22px] bg-brand transition-all" />
            <span class="block h-0.5 w-[22px] bg-brand transition-all" />
            <span class="block h-0.5 w-[22px] bg-brand transition-all" />
          </button>
        </div>
      </div>

      <!--
        모바일 패널(lg 미만) — 세로 레일.
        하위 화면이 2종 이상인 축(성적 분석)과 보관함은 아코디언, 나머지 축은 **1탭 = 이동**
        (하위 항목이 1개거나 같은 화면 앵커라 아코디언은 탭만 낭비한다).
        터치에는 hover 툴팁이 없으므로 상태를 텍스트로 병기한다.
      -->
      <nav
        v-show="mobileOpen"
        class="absolute inset-x-0 top-header flex max-h-[calc(100vh-theme(spacing.header))] flex-col items-stretch overflow-y-auto border-b border-line bg-white px-8 pb-[18px] pt-1.5 shadow-md lg:hidden"
        aria-label="AI 입시분석 메뉴(모바일)"
        @click.stop
      >
        <template v-for="axis in axes" :key="`m-${axis.key}`">
          <!-- 아코디언 축 — 하위 화면 2종 이상. 헤더 줄은 보관함과 같은 구성 + 순번·점·상태. -->
          <div v-if="axis.items.length > 0" class="border-b border-line">
            <button
              type="button"
              class="flex w-full items-center gap-2 py-[15px] text-left text-[16.5px] font-semibold transition-colors hover:text-brand"
              :class="isAxisActive(axis) ? 'text-brand' : 'text-body'"
              :aria-expanded="mobileAccordion === axis.key"
              @click="toggleMobileAccordion(axis.key)"
            >
              <span class="text-[12px] font-bold tabular-nums text-muted" aria-hidden="true">
                {{ axis.seq }}
              </span>
              <span class="flex-1">{{ axis.label }}</span>
              <WorkspaceRailDot :state="axis.state" />
              <span v-if="axis.hint" class="text-[12px] font-medium text-muted">
                {{ axis.state === 'filled' ? '채워짐' : '비어 있음' }}
              </span>
              <ChevronDown
                :size="18"
                :stroke-width="2.2"
                class="transition-transform"
                :class="mobileAccordion === axis.key ? 'rotate-180' : ''"
              />
            </button>
            <div v-show="mobileAccordion === axis.key" class="pb-2">
              <RouterLink
                v-for="item in axis.items"
                :key="`m-${axis.key}-${item.label}`"
                :to="item.route"
                class="block py-[11px] pl-3 text-[14.5px] font-semibold transition-colors hover:text-brand"
                :class="isSubItemActive(item) ? 'text-brand' : 'text-body'"
                :aria-current="isSubItemActive(item) ? 'page' : undefined"
                @click="onNavigate"
              >
                {{ item.label }}
              </RouterLink>
            </div>
          </div>

          <!-- 직접 링크 축 -->
          <RouterLink
            v-else
            :to="axis.route"
            class="flex items-center gap-2 border-b border-line py-[15px] text-left text-[16.5px] font-semibold transition-colors hover:text-brand"
            :class="isAxisActive(axis) ? 'text-brand' : 'text-body'"
            :aria-current="isAxisActive(axis) ? 'page' : undefined"
            @click="onNavigate"
          >
            <span class="text-[12px] font-bold tabular-nums text-muted" aria-hidden="true">
              {{ axis.seq }}
            </span>
            <span class="flex-1">{{ axis.label }}</span>
            <WorkspaceRailDot :state="axis.state" />
            <span v-if="axis.hint" class="text-[12px] font-medium text-muted">
              {{ axis.state === 'filled' ? '채워짐' : '비어 있음' }}
            </span>
          </RouterLink>
        </template>

        <!-- 보관함 — 레일 밖 아코디언(`?kind=` 필터 딥링크). -->
        <div class="border-b border-line">
          <button
            type="button"
            class="flex w-full items-center justify-between py-[15px] text-left text-[16.5px] font-semibold transition-colors hover:text-brand"
            :class="route.path.startsWith('/reports') ? 'text-brand' : 'text-body'"
            :aria-expanded="mobileAccordion === MOBILE_ARCHIVE_KEY"
            @click="toggleMobileAccordion(MOBILE_ARCHIVE_KEY)"
          >
            {{ RAIL_ARCHIVE.label }}
            <ChevronDown
              :size="18"
              :stroke-width="2.2"
              class="transition-transform"
              :class="mobileAccordion === MOBILE_ARCHIVE_KEY ? 'rotate-180' : ''"
            />
          </button>
          <div v-show="mobileAccordion === MOBILE_ARCHIVE_KEY" class="pb-2">
            <RouterLink
              v-for="item in RAIL_ARCHIVE.items"
              :key="`m-${item.label}`"
              :to="item.route"
              class="block py-[11px] pl-3 text-[14.5px] font-semibold transition-colors hover:text-brand"
              :class="isSubItemActive(item) ? 'text-brand' : 'text-body'"
              @click="onNavigate"
            >
              {{ item.label }}
            </RouterLink>
          </div>
        </div>

        <!-- 모바일: 인증 분기 -->
        <template v-if="auth.isAuthenticated">
          <RouterLink
            to="/card"
            :class="buttonVariants({ variant: 'line', block: true, class: 'mt-4' })"
            @click="onNavigate"
          >
            마이페이지
          </RouterLink>
          <button
            type="button"
            :class="buttonVariants({ variant: 'line', block: true, class: 'mt-2' })"
            @click="onLogout"
          >
            로그아웃
          </button>
        </template>
        <RouterLink
          v-else
          :to="{ path: '/login', query: { redirect: brand.crossLinks.analysis } }"
          :class="buttonVariants({ variant: 'line', block: true, class: 'mt-4' })"
          @click="onNavigate"
        >
          로그인
        </RouterLink>
      </nav>
    </header>

    <main class="flex-1">
      <slot />
    </main>

    <!-- 푸터는 레이아웃이 주입(브랜드 공유 AppFooter). 셸은 자리만 제공 — layout→도메인 의존 회피. -->
    <slot name="footer" />
  </div>
</template>
